/**
 * Snapshot of every persistent {@link PlayerProfile} field. Storage
 * adapters serialise this to whatever backing store they use (localStorage
 * for web, AsyncStorage for native, server-side wallet for Phase D).
 */
/** Royal Road's story setting: `ask` until the player chooses. */
export type StoryMode = 'ask' | 'on' | 'off';
/** When Fen speaks up. */
export type FenChatter = 'big' | 'often';

export interface PlayerProfileSnapshot {
  balance: number;
  /** Epoch seconds of the last claimed daily bonus. {@code 0} means never. */
  lastLoginEpoch: number;
  /** Consecutive-day login streak. Resets to 1 if more than 48h pass between
   * claims (one missed day is forgiven via the bonus calc; two breaks the
   * streak). */
  loginStreak: number;
  /** Cumulative XP — Phase G.3. Grows by 1 per coin bet across all games
   * (auto-tracked in {@link PlayerProfile#setBalance} when balance drops).
   * Future phases unlock content (themes, premium games) at level
   * thresholds derived from XP. */
  xp: number;
  /** Has the player completed the first-launch onboarding tour? Phase H.
   * The mobile app shows a 4-step walkthrough to fresh players before the
   * first daily bonus modal; once dismissed (completed or skipped), this
   * stays {@code true} forever and the tour never shows again. */
  onboardingComplete: boolean;
  /** Premium currency — Phase I. Awarded on level-up, eventually purchasable
   * via IAP. Spent in the gem shop on premium-theme unlocks + coin packs. */
  gems: number;
  /** Theme ids the player has unlocked. Free themes (default, midnight)
   * are bootstrapped on profile creation; premium themes appear here only
   * after a successful gem-shop purchase. */
  unlockedThemeIds: readonly string[];
  /** Slot ids (stringified {@code WorldMapNode.id}) the player has purchased
   * with gems — T1 gem economy (2026-05-28). The first slot in each biome
   * is free by default; that's derived at read time from the shared
   * registry, not stored here. Newly added biomes don't need a backfill —
   * empty array on new profiles is the correct starting state.
   *
   * <p>Mirrored from the server-side {@code Wallet.unlockedSlotIds} in
   * multiplayer; carries solo-play unlocks in LocalStorage. */
  unlockedSlotIds: readonly string[];
  /** Biome ids of artefacts the player uncovered on the reels (2026-06-06).
   * The app-side finds were retired in October 2026: nothing adds to this
   * any more, but it is kept as loaded and written back, so older builds
   * still read it. Optional; defaults to empty. */
  collectedArtefactIds?: readonly string[];
  /** Slot node ids (stringified {@code WorldMapNode.id}) the player has
   * favourited (2026-06-20). Surfaced in the favourites banner above the
   * biome list + toggled from the heart on the single-player slot HUD.
   * Optional so profiles written before this field load cleanly with an
   * empty list. Part of the synced snapshot, so favourites follow the
   * player across devices + survive a reinstall with zero server change. */
  favouriteSlotIds?: readonly string[];
  /** Slot mastery (M1, 2026-06-26) — stringified {@code WorldMapNode.id}
   * → cumulative coins wagered on that slot. Stars + medal are derived at
   * read time from {@code STAR_THRESHOLDS}. Optional so profiles written
   * before this field load cleanly with an empty record. Solo-play only
   * for now (no server mirror yet). */
  slotWagered?: Readonly<Record<string, number>>;
  /** Special items (M2) the player found before the app-side finds were
   * retired (October 2026): kept as loaded and written back, never added
   * to. Older builds re-seed legendaries from {@link collectedArtefactIds}.
   * Optional; defaults to empty. */
  specialItemIds?: readonly string[];
  /** Mystery worlds the player unlocked (M3, retired with the finds): kept
   * as loaded and written back, never added to. Optional; defaults to empty. */
  unlockedBiomeIds?: readonly string[];
  /** Epoch seconds of the last claimed daily-quest reward — independent
   * of {@link lastLoginEpoch}, which tracks the launch DailyBonusModal.
   * The world-map daily-quest node uses this for its 24h cooldown.
   * {@code 0} means never claimed. T6 (mini-game pass). */
  lastDailyQuestEpoch: number;
  /** Audio preferences (producer audit Phase 4 — settings cog). The
   *  master toggle controls AudioBus#setMuted — affects every channel
   *  (SFX, music, ambient, wins). Default is on — playable on first
   *  launch, mute on opt-in. */
  soundEnabled: boolean;
  /** Music + ambient channel toggle (2026-06-04 producer brief).
   *  Independent of {@link soundEnabled} so a player can keep SFX
   *  on while killing the looping music bed. Defaults to on. */
  musicEnabled: boolean;
  /** Sound-effects channel toggle (2026-06-07). Independent of
   *  {@link soundEnabled} + {@link musicEnabled}. Defaults to on. */
  sfxEnabled: boolean;
  /** Gamble feature toggle (2026-06-04 producer brief). When false
   *  the slot scene auto-collects winnings + spin continues; the
   *  inline GAMBLE button + double-or-nothing card overlay never
   *  appear. Defaults to on. */
  gambleEnabled: boolean;
  /** The bet the player last chose on a slot, as a multiple of the slot's
   *  bet (1, 2, 4 or 8), so every slot starts there. Defaults to 1. */
  slotBetMultiple?: number;
  /** Royal Road's story (Phase 3): `ask` until the player chooses, then
   *  `on` (Fen and the story cards) or `off` (Classic mode). */
  storyMode?: StoryMode;
  /** When Fen speaks up: only at big moments, or often. */
  fenChatter?: FenChatter;
  /** Road slots (theme ids) the player has opened, so Fen greets each road
   *  that's new to them once. Optional; defaults to empty. */
  roadSlotsSeen?: readonly string[];
  /** The player has seen the one-time card that says every slot has a
   *  Wheel Run now (their first one on a slot that had the Sweet Trail).
   *  Optional; defaults to false. */
  wheelRunCardSeen?: boolean;
  /** 2026-05-31 follow-up — fresh profiles start with this flag
   *  {@code true} and {@code gems = 0}, so the {@code STARTER_GEM_GRANT}
   *  lands as a visible celebration when the player taps GOT IT on the
   *  "Gems unlock slots" onboarding step. Returning players (loaded
   *  from a pre-existing snapshot, even one written by code that
   *  predates this field) read as {@code false} — they already got
   *  their grant under the old constructor path, and we mustn't
   *  double-credit. Once {@code claimStarterGems()} fires, this flips
   *  to {@code false} permanently. */
  pendingStarterGems?: boolean;
}

/**
 * Adapter contract for persisting {@link PlayerProfileSnapshot}. Tests pass
 * an in-memory implementation; production hosts pass a localStorage
 * (or eventually server-backed) implementation.
 */
export interface ProfileStorage {
  load(): PlayerProfileSnapshot | null;
  save(snapshot: PlayerProfileSnapshot): void;
}

/**
 * In-memory adapter — useful for tests + headless contexts. Each instance
 * keeps its own state.
 */
export class InMemoryProfileStorage implements ProfileStorage {
  private snapshot: PlayerProfileSnapshot | null = null;
  load(): PlayerProfileSnapshot | null {
    return this.snapshot ? { ...this.snapshot } : null;
  }
  save(snapshot: PlayerProfileSnapshot): void {
    this.snapshot = { ...snapshot };
  }
}

/**
 * localStorage-backed adapter. Browsers + WebViews. Silently no-ops on
 * permission errors (private browsing on Safari, storage quota exceeded)
 * so the profile still functions in-memory.
 */
export class LocalStorageProfileStorage implements ProfileStorage {
  constructor(private readonly key: string = 'goodtime.profile') {}

  load(): PlayerProfileSnapshot | null {
    try {
      if (typeof localStorage === 'undefined') return null;
      const raw = localStorage.getItem(this.key);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Partial<PlayerProfileSnapshot>;
      // Validate the foundational fields. Newer fields are forward-
      // compatible — older snapshots without them get sensible defaults
      // rather than failing the load:
      //   * {@code xp} (G.3) → 0
      //   * {@code onboardingComplete} (H) → false
      //   * {@code gems} (I) → 0
      //   * {@code unlockedThemeIds} (I) → ['default', 'midnight']
      //   * {@code unlockedSlotIds} (T1, 2026-05-28) → []
      //   * {@code lastDailyQuestEpoch} (T6) → 0
      //   * {@code soundEnabled} (Phase 4 settings cog) → true
      //   * {@code slotWagered} (M1) → {}
      if (
        typeof parsed.balance !== 'number' ||
        typeof parsed.lastLoginEpoch !== 'number' ||
        typeof parsed.loginStreak !== 'number'
      ) {
        return null;
      }
      return {
        balance: parsed.balance,
        lastLoginEpoch: parsed.lastLoginEpoch,
        loginStreak: parsed.loginStreak,
        xp: typeof parsed.xp === 'number' ? parsed.xp : 0,
        onboardingComplete:
          typeof parsed.onboardingComplete === 'boolean' ? parsed.onboardingComplete : false,
        gems: typeof parsed.gems === 'number' ? parsed.gems : 0,
        unlockedThemeIds: Array.isArray(parsed.unlockedThemeIds)
          ? parsed.unlockedThemeIds.filter((id): id is string => typeof id === 'string')
          : ['default', 'midnight'],
        unlockedSlotIds: Array.isArray(parsed.unlockedSlotIds)
          ? parsed.unlockedSlotIds.filter((id): id is string => typeof id === 'string')
          : [],
        collectedArtefactIds: Array.isArray(parsed.collectedArtefactIds)
          ? parsed.collectedArtefactIds.filter((id): id is string => typeof id === 'string')
          : [],
        favouriteSlotIds: Array.isArray(parsed.favouriteSlotIds)
          ? parsed.favouriteSlotIds.filter((id): id is string => typeof id === 'string')
          : [],
        slotWagered:
          parsed.slotWagered && typeof parsed.slotWagered === 'object' && !Array.isArray(parsed.slotWagered)
            ? Object.fromEntries(
                Object.entries(parsed.slotWagered).filter(
                  ([, v]) => typeof v === 'number' && Number.isFinite(v) && v >= 0,
                ),
              )
            : {},
        // The retired finds: read so the next save writes them back.
        specialItemIds: Array.isArray(parsed.specialItemIds)
          ? parsed.specialItemIds.filter((id): id is string => typeof id === 'string')
          : [],
        unlockedBiomeIds: Array.isArray(parsed.unlockedBiomeIds)
          ? parsed.unlockedBiomeIds.filter((id): id is string => typeof id === 'string')
          : [],
        lastDailyQuestEpoch:
          typeof parsed.lastDailyQuestEpoch === 'number' ? parsed.lastDailyQuestEpoch : 0,
        soundEnabled:
          typeof parsed.soundEnabled === 'boolean' ? parsed.soundEnabled : true,
        musicEnabled:
          typeof parsed.musicEnabled === 'boolean' ? parsed.musicEnabled : true,
        sfxEnabled:
          typeof parsed.sfxEnabled === 'boolean' ? parsed.sfxEnabled : true,
        gambleEnabled:
          typeof parsed.gambleEnabled === 'boolean' ? parsed.gambleEnabled : true,
        slotBetMultiple:
          typeof parsed.slotBetMultiple === 'number' && parsed.slotBetMultiple >= 1
            ? parsed.slotBetMultiple
            : 1,
        storyMode: parsed.storyMode === 'on' || parsed.storyMode === 'off' ? parsed.storyMode : 'ask',
        fenChatter: parsed.fenChatter === 'big' ? 'big' : 'often',
        roadSlotsSeen: Array.isArray(parsed.roadSlotsSeen)
          ? parsed.roadSlotsSeen.filter((id): id is string => typeof id === 'string')
          : [],
        wheelRunCardSeen: parsed.wheelRunCardSeen === true,
        // pendingStarterGems — defaults to false on snapshots written
        // before this field existed. Those players already got their
        // grant under the pre-2026-05-31 constructor path, so leaving
        // it false is correct and prevents a double-credit.
        pendingStarterGems:
          typeof parsed.pendingStarterGems === 'boolean'
            ? parsed.pendingStarterGems
            : false,
      };
    } catch {
      return null;
    }
  }

  save(snapshot: PlayerProfileSnapshot): void {
    try {
      if (typeof localStorage === 'undefined') return;
      localStorage.setItem(this.key, JSON.stringify(snapshot));
    } catch {
      // Quota exceeded / disabled / private mode — silently degrade.
    }
  }
}
